/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { SVGProps } from 'react';
import { cn } from '@/lib/utils';

export interface CompanyLogoProps extends Omit<SVGProps<SVGSVGElement>, 'children' | 'role'> {
    decorative?: boolean;
    title?: string;
    inverse?: boolean;
}

export default function CompanyLogo({
    className,
    decorative = false,
    inverse = false,
    title = 'Ashley Furniture',
    ...props
}: CompanyLogoProps) {
    const accessibilityProps = decorative
        ? { 'aria-hidden': true }
        : {
              role: 'img',
              'aria-label': title,
          };

    return (
        <svg
            width="178"
            height="26"
            viewBox="0 0 178 26"
            xmlns="http://www.w3.org/2000/svg"
            className={cn('company-logo lg:h-7 w-auto', className)}
            {...accessibilityProps}
            {...props}>
            {!decorative && <title>{title}</title>}
            <path
                d="M77.6687 13.4785C73.684 13.1667 70.8659 12.8144 70.8659 11.1628C70.8659 10.7586 71.0218 10.4121 71.3394 10.1002C72.3962 9.05498 74.9256 8.70271 76.9411 8.72004C80.0191 8.74891 82.4792 9.55162 84.2578 11.1108L84.4542 11.2841L86.8103 8.74314L86.5966 8.55834C84.2347 6.56601 80.7929 5.46301 76.8891 5.46301C71.0738 5.46301 67.1642 7.83649 67.1642 11.3707C67.1642 15.5921 72.8929 16.5565 77.2067 16.9146C81.7111 17.2957 83.8536 17.7693 83.8536 19.4151C83.8536 21.136 81.6476 22.1235 77.8015 22.1235C74.1229 22.1235 71.2932 21.1245 68.8967 18.982L68.6945 18.803L66.373 21.4305L66.5809 21.6096C69.4453 24.0812 73.2798 25.3805 77.6745 25.3805C83.7843 25.3805 87.5784 22.9955 87.5784 19.1552C87.5784 14.8472 82.8141 13.877 77.6687 13.4785Z"
                className={inverse ? 'letter inverse' : 'letter'}
            />
            <path
                d="M107.669 13.6056H94.3928V6.01166H90.72V24.8262H94.3928V16.9666H107.669V24.8262H111.342V6.01166H107.669V13.6056Z"
                className={inverse ? 'letter inverse' : 'letter'}
            />
            <path
                d="M118.676 6.01166H115.003V24.8262H132.155V21.523H118.676V6.01166Z"
                className={inverse ? 'letter inverse' : 'letter'}
            />
            <path
                d="M138.744 16.9146H151.229V13.6345H138.744V9.29178H153.579V6.01166H135.1V24.8262H153.839V21.5461H138.744V16.9146Z"
                className={inverse ? 'letter inverse' : 'letter'}
            />
            <path
                d="M166.688 15.1013L159.851 6.01166H155.543L164.823 18.3295V24.8262H168.496V18.3295L177.753 6.01166H173.474L166.688 15.1013Z"
                className={inverse ? 'letter inverse' : 'letter'}
            />
            <path
                d="M48.5923 1.71515L36.0781 24.8262H40.3689L43.3083 19.2477H58.1324L61.1064 24.8262H65.4318L52.9523 1.71515H48.5923ZM45.0927 15.8809L50.7405 5.18007L56.3883 15.8809H45.0927Z"
                className={inverse ? 'letter inverse' : 'letter'}
            />
            <path
                d="M24.667 12.3755L12.4473 0.0577346C12.4358 0.0461848 12.4127 0.0461848 12.4011 0.0577346L0.175732 12.3755C0.169958 12.3813 0.164185 12.3929 0.164185 12.3986V24.7915C0.164185 24.8088 0.181508 24.8262 0.198832 24.8262H24.6439C24.6612 24.8262 24.6785 24.8088 24.6785 24.7915V12.3986C24.6785 12.3929 24.6727 12.3813 24.667 12.3755ZM21.202 21.0494C21.202 21.2862 21.1443 21.3497 20.9306 21.3497H3.88897C3.64065 21.3497 3.64065 21.1476 3.64065 21.0725V14.1888C3.64065 13.8366 3.64643 13.8193 3.87743 13.5767L3.88897 13.5652C4.11419 13.3284 12.0431 5.34752 12.1239 5.27244C12.2625 5.13385 12.3434 5.07032 12.43 5.07032C12.5224 5.07032 12.5975 5.13962 12.7014 5.24357C12.9324 5.47457 20.9653 13.5709 21.0461 13.6518C21.1905 13.8019 21.2078 13.8481 21.2078 14.0214V21.0494H21.202Z"
                fill="#F68C2C"
            />
        </svg>
    );
}
